import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Calculator, BookOpen, TrendingUp, Shield, Zap } from "lucide-react";
import { motion } from "framer-motion";

const Resources = () => {
  const blogPosts = [
    {
      title: "The Future of Enterprise Call Centers: AI Voice Automation",
      excerpt: "Discover how generative AI is transforming customer service operations and reducing costs by up to 40%.",
      date: "2025-01-15",
      category: "Industry Trends",
      icon: TrendingUp,
    },
    {
      title: "GDPR Compliance in AI Voice Agents: What You Need to Know",
      excerpt: "A comprehensive guide to maintaining regulatory compliance while deploying AI voice technology in Europe.",
      date: "2025-01-10",
      category: "Compliance",
      icon: Shield,
    },
    {
      title: "ROI of AI Voice Agents: A Data-Driven Analysis",
      excerpt: "Real-world case studies showing cost savings, efficiency gains, and customer satisfaction improvements.",
      date: "2025-01-05",
      category: "Business Value",
      icon: Calculator,
    },
  ];

  const resources = [
    {
      icon: FileText,
      title: "Technical Whitepaper",
      description: "Deep dive into our AI voice architecture, security protocols, and integration capabilities.",
      cta: "Download Whitepaper",
      gradient: "from-gradient-pink/20 to-gradient-purple/20",
    },
    {
      icon: Calculator,
      title: "ROI Calculator",
      description: "Calculate your potential cost savings and efficiency gains with Holionix AI voice agents.",
      cta: "Calculate ROI",
      gradient: "from-gradient-purple/20 to-gradient-blue/20",
    },
    {
      icon: BookOpen,
      title: "Case Studies",
      description: "See how industry leaders are transforming their operations with Holionix voice AI.",
      cta: "Read Case Studies",
      gradient: "from-gradient-blue/20 to-gradient-cyan/20",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-shadow-glow">
              Resources & <span className="gradient-brand-text">Insights</span>
            </h1>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              Stay informed with the latest thought leadership, technical insights, and industry best practices.
            </p>
          </motion.div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8">Latest Blog Posts</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 card-dark hover:glow-effect transition-all duration-300 h-full">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gradient-pink/20 to-gradient-cyan/20 flex items-center justify-center mb-4">
                      <post.icon className="w-6 h-6 text-accent" />
                    </div>
                    <div className="text-sm text-accent mb-2">{post.category} • {post.date}</div>
                    <h3 className="text-xl font-bold mb-3">{post.title}</h3>
                    <p className="text-foreground/70 mb-4">{post.excerpt}</p>
                    <Button variant="link" className="text-accent p-0 hover:text-accent/80">
                      Read More →
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold mb-8">Downloadable Resources</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {resources.map((resource, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`p-8 card-dark gradient-border hover:glow-effect transition-all duration-300 h-full bg-gradient-to-br ${resource.gradient}`}>
                    <div className="w-14 h-14 rounded-lg gradient-brand flex items-center justify-center mb-6">
                      <resource.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{resource.title}</h3>
                    <p className="text-foreground/70 mb-6">{resource.description}</p>
                    <Button className="w-full gradient-brand text-white hover:opacity-90">
                      {resource.cta}
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          <Card className="mt-16 p-12 card-dark gradient-border text-center glow-effect">
            <Zap className="w-16 h-16 text-accent mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Subscribe to Our Newsletter</h2>
            <p className="text-foreground/70 mb-6 max-w-2xl mx-auto">
              Get the latest insights on AI voice automation, industry trends, and product updates delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-grow px-4 py-3 rounded-lg border border-input bg-card text-foreground focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
              />
              <Button className="gradient-brand text-white hover:opacity-90">
                Subscribe
              </Button>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Resources;
